import React, { useEffect, useRef, useState } from "react";
import { Modal } from "antd";
import dayjs from "dayjs";

const ENTRY_MODE_OPTIONS = [
    { label: "手工录入", value: "MANUAL" },
    { label: "自动采集", value: "AUTO" },
];

const PRICE_ROW_DEFINITIONS = [
    { key: "global", label: "全球 (Global)" },
    { key: "north_america", label: "北美 (N. America)" },
    { key: "europe", label: "欧洲 (Europe)" },
    { key: "asia_oceania", label: "亚太 (Asia/Oceania)" },
    { key: "middle_east", label: "中东 (Middle East)" },
    { key: "latin_america", label: "拉美 (Latin America)" },
    { key: "africa", label: "非洲 (Africa)" },
    { key: "cis", label: "独联体 (CIS)" },
];

const headerStyle = {
    padding: "16px 24px",
    borderBottom: "1px solid #f0f0f0",
    fontSize: 16,
    fontWeight: 500,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    position: "sticky",
    top: 0,
    background: "#fff",
    zIndex: 1,
};

const closeButtonStyle = {
    cursor: "pointer",
    fontSize: 18,
    color: "rgba(0, 0, 0, 0.45)",
    background: "none",
    border: "none",
    lineHeight: 1,
};

const bodyStyle = {
    padding: "24px",
};

const footerStyle = {
    padding: "12px 24px",
    borderTop: "1px solid #f0f0f0",
    display: "flex",
    justifyContent: "flex-end",
    gap: 8,
    position: "sticky",
    bottom: 0,
    background: "#fff",
    zIndex: 1,
};

const baseButtonStyle = {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    padding: "4px 12px",
    borderRadius: 4,
    border: "1px solid #d9d9d9",
    background: "#fff",
    cursor: "pointer",
    fontSize: 14,
    color: "rgba(0, 0, 0, 0.88)",
    height: 32,
    lineHeight: 1,
};

const primaryButtonStyle = {
    ...baseButtonStyle,
    background: "#1677ff",
    color: "#fff",
    borderColor: "#1677ff",
};

const formRowStyle = {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    columnGap: 16,
    marginBottom: 12,
};

const formItemStyle = {
    display: "flex",
    flexDirection: "column",
    gap: 4,
    flex: 1,
    minWidth: 0,
};

const formLabelStyle = {
    fontSize: 13,
    color: "rgba(0, 0, 0, 0.45)",
    lineHeight: "22px",
    marginBottom: 4,
};

const topInputStyle = {
    height: 32,
    border: "1px solid #d9d9d9",
    borderRadius: 4,
    padding: "0 8px",
    fontSize: 14,
    outline: "none",
    width: "100%",
    boxSizing: "border-box",
};

const tableStyle = {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 13,
};

const tableHeadCellStyle = {
    background: "#fafafa",
    padding: "8px 12px",
    fontWeight: 500,
    borderBottom: "1px solid #f0f0f0",
};

const tableBodyCellStyle = {
    padding: "8px 12px",
    borderBottom: "1px solid #f0f0f0",
};

const priceInputStyle = {
    width: 100,
    height: 28,
    border: "1px solid #d9d9d9",
    borderRadius: 4,
    padding: "0 8px",
    textAlign: "right",
    fontSize: 13,
    outline: "none",
};

const toInputValue = (value) => {
    if (value == null || value === "") {
        return "";
    }
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? String(numericValue) : "";
};

const parseDecimal = (value) => {
    if (value == null) {
        return undefined;
    }
    const normalized = String(value).replace(/,/g, "").trim();
    if (!normalized) {
        return undefined;
    }
    const numericValue = Number(normalized);
    return Number.isFinite(numericValue) ? Number(numericValue.toFixed(2)) : undefined;
};

const convertCentsPerGallonToDollar = (value) => {
    if (value == null) {
        return undefined;
    }
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? Number((numericValue / 100).toFixed(4)) : undefined;
};

const getChartModel = (initialValues) => initialValues?.raw_payload?.chart_parse?.model_output || {};

const buildRowValue = (initialValues, priceTableRows, chartModel, rowKey) => {
    const storedRow = priceTableRows?.[rowKey];
    if (storedRow) {
        return {
            price_per_mt: toInputValue(storedRow.price_per_mt),
            price_per_bbl: toInputValue(storedRow.price_per_bbl),
            price_per_gallon: toInputValue(storedRow.price_per_gallon),
        };
    }

    const chartMappings = {
        global: chartModel.jet_fuel_price,
        north_america: chartModel.north_america,
        europe: chartModel.europe || chartModel.europe_cis,
        asia_oceania: chartModel.asia_oceania,
        middle_east: chartModel.middle_east,
        latin_america: chartModel.latin_america || chartModel.latin_america_caribbean,
        africa: chartModel.africa,
        cis: chartModel.cis,
    };

    const topLevelMappings = {
        global: {
            price_per_mt: initialValues?.global_price_per_mt,
            price_per_bbl: initialValues?.global_price_per_bbl,
        },
        north_america: {
            price_per_mt: initialValues?.north_america_price_per_mt,
        },
        europe: {
            price_per_mt: initialValues?.europe_price_per_mt,
        },
        asia_oceania: {
            price_per_mt: initialValues?.asia_oceania_price_per_mt,
        },
    };

    const chartRow = chartMappings[rowKey] || {};
    const topLevelRow = topLevelMappings[rowKey] || {};

    return {
        price_per_mt: toInputValue(topLevelRow.price_per_mt ?? chartRow.usd_per_t),
        price_per_bbl: toInputValue(topLevelRow.price_per_bbl ?? chartRow.usd_per_bbl),
        price_per_gallon: toInputValue(convertCentsPerGallonToDollar(chartRow.cts_per_gal)),
    };
};

const buildInitialFormState = (initialValues) => {
    const rawPayload = initialValues?.raw_payload || {};
    const storedPriceTable = rawPayload.price_table || {};
    const priceTableRows = storedPriceTable.rows || storedPriceTable;
    const chartModel = getChartModel(initialValues);

    const priceTable = PRICE_ROW_DEFINITIONS.reduce((result, item) => {
        result[item.key] = buildRowValue(initialValues, priceTableRows, chartModel, item.key);
        return result;
    }, {});

    return {
        week_end_date: initialValues?.week_end_date || "",
        entry_mode: storedPriceTable.entry_mode || (["CRAWLER_AUTO", "OCR_AUTO"].includes(initialValues?.source_type) ? "AUTO" : "MANUAL"),
        price_table: priceTable,
    };
};

const resolveSourceType = (entryMode, initialValues) => {
    if (entryMode === "AUTO") {
        return initialValues?.source_type === "OCR_AUTO" ? "OCR_AUTO" : "CRAWLER_AUTO";
    }
    if (initialValues?.source_type && !["MANUAL", "MANUAL_PATCH"].includes(initialValues.source_type)) {
        return "MANUAL_PATCH";
    }
    return initialValues?.source_type === "MANUAL_PATCH" ? "MANUAL_PATCH" : "MANUAL";
};

const normalizePriceTable = (priceTable) => {
    return PRICE_ROW_DEFINITIONS.reduce((result, item) => {
        const row = priceTable?.[item.key] || {};
        result[item.key] = {
            price_per_mt: parseDecimal(row.price_per_mt),
            price_per_bbl: parseDecimal(row.price_per_bbl),
            price_per_gallon: parseDecimal(row.price_per_gallon),
        };
        return result;
    }, {});
};

const getResolvedStatus = (initialValues) => {
    if (initialValues?.status) {
        return initialValues.status;
    }
    return "PENDING_CONFIRM";
};

const FuelPriceFormModal = ({ open, initialValues, onCancel, onSubmit, submitting }) => {
    const [formState, setFormState] = useState(buildInitialFormState(initialValues));
    const formRef = useRef(null);

    useEffect(() => {
        if (!open) {
            setFormState(buildInitialFormState(initialValues));
            return;
        }
        setFormState(buildInitialFormState(initialValues));
    }, [initialValues, open]);

    const updateField = (fieldName, value) => {
        setFormState((current) => ({
            ...current,
            [fieldName]: value,
        }));
    };

    const updatePriceField = (rowKey, fieldName, value) => {
        setFormState((current) => ({
            ...current,
            price_table: {
                ...current.price_table,
                [rowKey]: {
                    ...current.price_table[rowKey],
                    [fieldName]: value,
                },
            },
        }));
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        if (!formRef.current?.reportValidity()) {
            return;
        }

        const normalizedPriceTable = normalizePriceTable(formState.price_table);
        const nextRawPayload = {
            ...(initialValues?.raw_payload || {}),
            price_table: {
                entry_mode: formState.entry_mode,
                rows: normalizedPriceTable,
            },
        };

        onSubmit({
            week_end_date: formState.week_end_date || undefined,
            publish_date: initialValues?.publish_date || (formState.week_end_date ? dayjs(formState.week_end_date).format("YYYY-MM-DD") : undefined),
            global_price_per_mt: normalizedPriceTable.global.price_per_mt,
            global_price_per_bbl: normalizedPriceTable.global.price_per_bbl,
            north_america_price_per_mt: normalizedPriceTable.north_america.price_per_mt,
            europe_price_per_mt: normalizedPriceTable.europe.price_per_mt,
            asia_oceania_price_per_mt: normalizedPriceTable.asia_oceania.price_per_mt,
            source_type: resolveSourceType(formState.entry_mode, initialValues),
            status: getResolvedStatus(initialValues),
            source_url: initialValues?.source_url,
            source_captured_at: initialValues?.source_captured_at,
            layout_column_middle_html: initialValues?.layout_column_middle_html,
            layout_column_middle_text: initialValues?.layout_column_middle_text,
            raw_payload: nextRawPayload,
            parse_confidence: initialValues?.parse_confidence,
            remark: initialValues?.remark,
        });
    };

    return (
        <Modal
            open={open}
            onCancel={onCancel}
            width={700}
            title={null}
            footer={null}
            closable={false}
            destroyOnHidden
            maskClosable={false}
            styles={{
                content: { padding: 0 },
                body: { padding: 0 },
            }}
        >
            <form ref={formRef} onSubmit={handleSubmit}>
                <div style={headerStyle}>
                    <span>录入周油价数据</span>
                    <button type="button" style={closeButtonStyle} onClick={onCancel}>
                        ✕
                    </button>
                </div>

                <div style={bodyStyle}>
                    <div style={formRowStyle}>
                        <div style={formItemStyle}>
                            <label style={formLabelStyle}>周结束日期</label>
                            <input
                                type="date"
                                required
                                value={formState.week_end_date}
                                onChange={(event) => updateField("week_end_date", event.target.value)}
                                style={topInputStyle}
                            />
                        </div>
                        <div style={formItemStyle}>
                            <label style={formLabelStyle}>录入方式</label>
                            <select
                                value={formState.entry_mode}
                                onChange={(event) => updateField("entry_mode", event.target.value)}
                                style={topInputStyle}
                            >
                                {ENTRY_MODE_OPTIONS.map((item) => (
                                    <option key={item.value} value={item.value}>
                                        {item.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div style={{ height: 1, background: "#f0f0f0", margin: "16px 0" }} />

                    <h4 style={{ fontSize: 14, fontWeight: 500, marginBottom: 12 }}>价格表数据 ($/MT)</h4>

                    <table style={tableStyle}>
                        <thead>
                            <tr>
                                <th style={{ ...tableHeadCellStyle, textAlign: "left" }}>地区</th>
                                <th style={{ ...tableHeadCellStyle, textAlign: "right" }}>$/MT</th>
                                <th style={{ ...tableHeadCellStyle, textAlign: "right" }}>$/bbl</th>
                                <th style={{ ...tableHeadCellStyle, textAlign: "right" }}>$/gallon</th>
                            </tr>
                        </thead>
                        <tbody>
                            {PRICE_ROW_DEFINITIONS.map((row, index) => (
                                <tr key={row.key}>
                                    <td
                                        style={{
                                            ...tableBodyCellStyle,
                                            textAlign: "left",
                                            borderBottom: index === PRICE_ROW_DEFINITIONS.length - 1 ? "none" : tableBodyCellStyle.borderBottom,
                                        }}
                                    >
                                        {row.label}
                                    </td>
                                    <td
                                        style={{
                                            ...tableBodyCellStyle,
                                            textAlign: "right",
                                            borderBottom: index === PRICE_ROW_DEFINITIONS.length - 1 ? "none" : tableBodyCellStyle.borderBottom,
                                        }}
                                    >
                                        <input
                                            type="number"
                                            step="0.01"
                                            required={row.key === "global"}
                                            placeholder={row.key === "global" ? "698.50" : ""}
                                            value={formState.price_table[row.key]?.price_per_mt || ""}
                                            onChange={(event) => updatePriceField(row.key, "price_per_mt", event.target.value)}
                                            style={priceInputStyle}
                                        />
                                    </td>
                                    <td
                                        style={{
                                            ...tableBodyCellStyle,
                                            textAlign: "right",
                                            borderBottom: index === PRICE_ROW_DEFINITIONS.length - 1 ? "none" : tableBodyCellStyle.borderBottom,
                                        }}
                                    >
                                        <input
                                            type="number"
                                            step="0.01"
                                            placeholder={row.key === "global" ? "205.20" : ""}
                                            value={formState.price_table[row.key]?.price_per_bbl || ""}
                                            onChange={(event) => updatePriceField(row.key, "price_per_bbl", event.target.value)}
                                            style={priceInputStyle}
                                        />
                                    </td>
                                    <td
                                        style={{
                                            ...tableBodyCellStyle,
                                            textAlign: "right",
                                            borderBottom: index === PRICE_ROW_DEFINITIONS.length - 1 ? "none" : tableBodyCellStyle.borderBottom,
                                        }}
                                    >
                                        <input
                                            type="number"
                                            step="0.01"
                                            placeholder={row.key === "global" ? "2.22" : ""}
                                            value={formState.price_table[row.key]?.price_per_gallon || ""}
                                            onChange={(event) => updatePriceField(row.key, "price_per_gallon", event.target.value)}
                                            style={priceInputStyle}
                                        />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div style={footerStyle}>
                    <button type="button" style={baseButtonStyle} onClick={onCancel} disabled={submitting}>
                        取消
                    </button>
                    <button type="submit" style={{ ...primaryButtonStyle, opacity: submitting ? 0.6 : 1 }} disabled={submitting}>
                        保存
                    </button>
                </div>
            </form>
        </Modal>
    );
};

export default FuelPriceFormModal;
